import { useState, useRef, useCallback, useEffect } from 'react';
import { collection, addDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const REQUEST_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes - Osiris only polls every 5s
                                            // and generation itself takes real time,
                                            // but a stuck daemon shouldn't spin forever.

export const DownloadReportButton = () => {
  const [state, setState] = useState('idle'); // idle | waiting | error
  const [errorMessage, setErrorMessage] = useState(null);
  const unsubscribeRef = useRef(null);
  const timeoutRef = useRef(null);

  const cleanup = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Stop listening if the component unmounts mid-request
  useEffect(() => () => cleanup(), [cleanup]);

  const handleDownload = useCallback(async () => {
    if (state === 'waiting') return;
    setState('waiting');
    setErrorMessage(null);

    try {
      const requestRef = await addDoc(collection(db, 'report_requests'), {
        status: 'pending',
        requestedAt: serverTimestamp(),
        requestedBy: auth.currentUser?.email || null,
      });

      timeoutRef.current = setTimeout(() => {
        cleanup();
        setErrorMessage('Report generation timed out. Please try again.');
        setState('error');
      }, REQUEST_TIMEOUT_MS);

      unsubscribeRef.current = onSnapshot(
        requestRef,
        (snapshot) => {
          const data = snapshot.data();
          if (!data) return;

          if (data.status === 'ready' && data.downloadUrl) {
            cleanup();
            window.location.href = data.downloadUrl;
            setState('idle');
          } else if (data.status === 'error') {
            cleanup();
            setErrorMessage(data.error || 'Report generation failed.');
            setState('error');
          }
        },
        (error) => {
          cleanup();
          setErrorMessage(error.message);
          setState('error');
        }
      );
    } catch (error) {
      cleanup();
      setErrorMessage(error.message);
      setState('error');
    }
  }, [state, cleanup]);

  return (
    <div className="d-flex align-items-center">
      <button
        type="button"
        className="btn btn-link px-2"
        onClick={handleDownload}
        disabled={state === 'waiting'}
      >
        {state === 'waiting' ? 'Generating report…' : 'Download daily report now'}
      </button>
      {state === 'error' && (
        <span className="text-danger small ms-2">{errorMessage}</span>
      )}
    </div>
  );
};
